import React, { useCallback, useState } from 'react';
import { Droplet, Save, RefreshCw, Download, Calculator, Settings, Ruler } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import PipeParametersSection from './PipeParametersSection';
import CalculationTypeSection from './CalculationTypeSection';
import AdditionalParametersSection from './AdditionalParametersSection';
import LocalResistanceSection from './LocalResistanceSection';
import ElevationSection from './ElevationSection';

// Импорт утилитарных функций
import { isPositiveNumber } from '../../../../../utils/validation';
import { calculateFlowRate, calculatePressureDrop, calculatePipeDiameter, calculateReynoldsNumber } from '../../../../../utils/calculations/hydraulics';

/**
 * Компонент основной формы расчета расхода воды в трубах
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  pipeTypes,
  connectionTypes,
  standardDiameters,
  saveCalculation 
}) => {
  
  // Состояние для имени сохраняемого расчета
  const [saveCalcName, setSaveCalcName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };

      // Обновляем значение шероховатости автоматически при изменении типа трубы
      if (name === 'pipeType') {
        const selectedPipeType = pipeTypes.find(type => type.id === fieldValue);
        if (selectedPipeType) {
          const currentRoughness = parseFloatOrDefault(prev.pipeRoughness, 0);
          const isRoughnessDefault = pipeTypes.some(pt => currentRoughness === pt.defaultRoughness);
          if (isRoughnessDefault || !prev.pipeRoughness || currentRoughness <= 0) {
            newState.pipeRoughness = selectedPipeType.defaultRoughness.toString();
          }
        }
      }

      // Сбрасываем связанные поля если функция отключена
      if (name === 'includeLocalResistance' && !checked) {
        newState.bendCount = '0';
        newState.valveCount = '0';
        newState.teeCount = '0';
        newState.entranceType = 'standard';
        newState.exitType = 'standard';
      }
      
      if (name === 'includeElevationChange' && !checked) {
        newState.elevationChange = '0';
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [pipeTypes, errors, setErrors, setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      pipeInnerDiameter, pipeLength, pipeRoughness, systemPressure, waterTemperature,
      calculationType, pressureDrop, flowRate, requiredFlowRate, maxPressureDrop,
      includeLocalResistance, bendCount, valveCount, teeCount, 
      includeElevationChange, elevationChange
    } = formData;

    // Парсинг числовых значений
    const numPipeInnerDiameter = parseFloatOrDefault(pipeInnerDiameter, -1);
    const numPipeLength = parseFloatOrDefault(pipeLength, -1);
    const numPipeRoughness = parseFloatOrDefault(pipeRoughness, -1);
    const numSystemPressure = parseFloatOrDefault(systemPressure, -1);
    const numWaterTemperature = parseFloatOrDefault(waterTemperature, -1);

    // Валидация базовых полей
    if (numPipeInnerDiameter <= 0) newErrors.pipeInnerDiameter = 'Диаметр должен быть положительным.';
    if (!newErrors.pipeInnerDiameter && numPipeInnerDiameter > 2000) newErrors.pipeInnerDiameter = 'Максимальный диаметр 2000 мм.';
    
    if (numPipeLength <= 0) newErrors.pipeLength = 'Длина должна быть положительной.';
    if (!newErrors.pipeLength && numPipeLength > 10000) newErrors.pipeLength = 'Максимальная длина 10 км.';
    
    if (numPipeRoughness < 0) newErrors.pipeRoughness = 'Шероховатость не может быть отрицательной.';
    if (!newErrors.pipeRoughness && numPipeRoughness > 10) newErrors.pipeRoughness = 'Шероховатость не может быть более 10 мм.';
    
    if (numSystemPressure <= 0) newErrors.systemPressure = 'Давление должно быть положительным.';
    if (!newErrors.systemPressure && numSystemPressure > 100) newErrors.systemPressure = 'Давление не может быть более 100 бар.';
    
    if (numWaterTemperature < 0) newErrors.waterTemperature = 'Температура не может быть отрицательной.';
    if (!newErrors.waterTemperature && numWaterTemperature > 100) newErrors.waterTemperature = 'Температура не может быть более 100°C.';

    // Валидация в зависимости от типа расчета
    if (calculationType === 'flowRate') {
      const numPressureDrop = parseFloatOrDefault(pressureDrop, -1);
      if (numPressureDrop <= 0) newErrors.pressureDrop = 'Перепад давления должен быть положительным.';
      if (!newErrors.pressureDrop && numPressureDrop > numSystemPressure) 
        newErrors.pressureDrop = `Перепад давления (${numPressureDrop} бар) не может быть больше давления системы (${numSystemPressure} бар).`;
    } 
    else if (calculationType === 'pressure') {
      const numFlowRate = parseFloatOrDefault(flowRate, -1);
      if (numFlowRate <= 0) newErrors.flowRate = 'Расход должен быть положительным.';
      if (!newErrors.flowRate && numFlowRate > 1000) newErrors.flowRate = 'Максимальный расход 1000 м³/ч.';
    } 
    else if (calculationType === 'diameter') {
      const numRequiredFlowRate = parseFloatOrDefault(requiredFlowRate, -1);
      const numMaxPressureDrop = parseFloatOrDefault(maxPressureDrop, -1);
      
      if (numRequiredFlowRate <= 0) newErrors.requiredFlowRate = 'Расход должен быть положительным.';
      if (!newErrors.requiredFlowRate && numRequiredFlowRate > 1000) newErrors.requiredFlowRate = 'Максимальный расход 1000 м³/ч.';
      
      if (numMaxPressureDrop <= 0) newErrors.maxPressureDrop = 'Перепад давления должен быть положительным.';
      if (!newErrors.maxPressureDrop && numMaxPressureDrop > numSystemPressure) 
        newErrors.maxPressureDrop = `Перепад давления (${numMaxPressureDrop} бар) не может быть больше давления системы (${numSystemPressure} бар).`;
    }

    // Валидация местных сопротивлений
    if (includeLocalResistance) {
      if (!isPositiveNumber(bendCount)) newErrors.bendCount = 'Должно быть положительным числом.';
      if (!isPositiveNumber(valveCount)) newErrors.valveCount = 'Должно быть положительным числом.';
      if (!isPositiveNumber(teeCount)) newErrors.teeCount = 'Должно быть положительным числом.';
    }

    // Валидация изменения высоты
    if (includeElevationChange) {
      if (!elevationChange || isNaN(parseFloatOrDefault(elevationChange))) 
        newErrors.elevationChange = 'Введите корректное значение.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Получение коэффициентов местных сопротивлений
  const getLocalResistanceCoefficients = () => {
    // Стандартные коэффициенты сопротивления
    const bendCoefficient = 0.3;  // Поворот 90°
    const valveCoefficient = 3.0;  // Вентиль
    const teeCoefficient = 1.5;  // Тройник (проход)
    
    // Коэффициенты для входа и выхода
    const entranceType = formData.entranceType;
    const exitType = formData.exitType;
    
    const entranceFactor = connectionTypes.find(ct => ct.id === entranceType)?.factor || 0.5;
    const exitFactor = connectionTypes.find(ct => ct.id === exitType)?.factor || 1.0;
    
    const bendCount = parseFloatOrDefault(formData.bendCount, 0);
    const valveCount = parseFloatOrDefault(formData.valveCount, 0);
    const teeCount = parseFloatOrDefault(formData.teeCount, 0);
    
    // Суммарный коэффициент местных сопротивлений
    return {
      bendResistance: bendCount * bendCoefficient,
      valveResistance: valveCount * valveCoefficient, 
      teeResistance: teeCount * teeCoefficient,
      entranceResistance: entranceFactor,
      exitResistance: exitFactor,
      totalResistance: bendCount * bendCoefficient + 
                      valveCount * valveCoefficient + 
                      teeCount * teeCoefficient + 
                      entranceFactor + 
                      exitFactor
    };
  };

  // Функция расчета
  const calculateResults = useCallback(() => {
    if (!validateForm()) {
      // Опционально прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Подготавливаем входные данные ---
        const pipeInnerDiameter = parseFloatOrDefault(formData.pipeInnerDiameter) / 1000; // Перевод мм в м
        const pipeLength = parseFloatOrDefault(formData.pipeLength);
        const pipeRoughness = parseFloatOrDefault(formData.pipeRoughness) / 1000; // Перевод мм в м
        const pipeType = formData.pipeType;
        const systemPressure = parseFloatOrDefault(formData.systemPressure); // бар
        const waterTemperature = parseFloatOrDefault(formData.waterTemperature); // °C
        const calculationType = formData.calculationType;
        
        // Физические свойства для расчетов
        const selectedPipeType = pipeTypes.find(t => t.id === pipeType);
        const pipeDensityFactor = selectedPipeType?.densityFactor || 1.0;
        
        // Расчет для местных сопротивлений
        const includeLocalResistance = formData.includeLocalResistance;
        const localResistanceCoefficients = getLocalResistanceCoefficients();
        const totalLocalResistance = includeLocalResistance ? localResistanceCoefficients.totalResistance : 0;
        
        // Расчет для перепада высот
        const includeElevationChange = formData.includeElevationChange;
        const elevationChange = includeElevationChange ? parseFloatOrDefault(formData.elevationChange, 0) : 0;

        let calculationResult = {};
        let standardRecommendedDiameter = null;
        
        // --- Выполняем расчеты в зависимости от типа ---
        if (calculationType === 'flowRate') {
          const pressureDrop = parseFloatOrDefault(formData.pressureDrop); // бар
          
          // Расчет расхода воды
          calculationResult = calculateFlowRate({
            diameter: pipeInnerDiameter,
            length: pipeLength,
            roughness: pipeRoughness,
            pressureDrop: pressureDrop,
            temperature: waterTemperature,
            localResistance: totalLocalResistance,
            elevationChange: elevationChange
          });
          
        } else if (calculationType === 'pressure') {
          const flowRate = parseFloatOrDefault(formData.flowRate); // м³/ч
          
          // Расчет перепада давления
          calculationResult = calculatePressureDrop({
            diameter: pipeInnerDiameter,
            length: pipeLength,
            roughness: pipeRoughness,
            flowRate: flowRate,
            temperature: waterTemperature,
            localResistance: totalLocalResistance,
            elevationChange: elevationChange
          });
          
        } else if (calculationType === 'diameter') {
          const requiredFlowRate = parseFloatOrDefault(formData.requiredFlowRate); // м³/ч
          const maxPressureDrop = parseFloatOrDefault(formData.maxPressureDrop); // бар
          
          // Расчет оптимального диаметра трубы
          calculationResult = calculatePipeDiameter({
            flowRate: requiredFlowRate,
            length: pipeLength,
            roughness: pipeRoughness,
            maxPressureDrop: maxPressureDrop,
            temperature: waterTemperature,
            localResistance: totalLocalResistance,
            elevationChange: elevationChange
          });
          
          // Получение ближайшего стандартного диаметра
          const pipeTypeStandardDiameters = standardDiameters[pipeType] || [];
          const theoreticalDiameterMm = calculationResult.diameter * 1000; // Перевод в мм
          
          // Находим ближайший больший стандартный диаметр
          for (let i = 0; i < pipeTypeStandardDiameters.length; i++) {
            if (pipeTypeStandardDiameters[i] >= theoreticalDiameterMm) {
              standardRecommendedDiameter = pipeTypeStandardDiameters[i];
              break;
            }
          }
          
          // Если все стандартные диаметры меньше расчетного, берем наибольший
          if (!standardRecommendedDiameter && pipeTypeStandardDiameters.length > 0) {
            standardRecommendedDiameter = pipeTypeStandardDiameters[pipeTypeStandardDiameters.length - 1];
          }
        }
        
        // Расчет числа Рейнольдса для всех типов расчетов
        const reynoldsNumber = calculateReynoldsNumber({
          diameter: pipeInnerDiameter, 
          flowRate: calculationResult.flowRate || parseFloatOrDefault(formData.flowRate),
          temperature: waterTemperature
        });
        
        // Определения типа потока
        let flowType = "Ламинарный";
        if (reynoldsNumber >= 4000) {
          flowType = "Турбулентный";
        } else if (reynoldsNumber >= 2300) {
          flowType = "Переходный";
        }
        
        // --- Подготовка результатов ---
        const finalResults = {
          ...calculationResult,
          reynoldsNumber,
          flowType,
          
          // Входные параметры для отображения в результатах
          pipeInnerDiameter: pipeInnerDiameter * 1000, // Обратно в мм для отображения
          pipeLength,
          pipeRoughness: pipeRoughness * 1000, // Обратно в мм для отображения
          pipeType,
          systemPressure,
          waterTemperature,
          calculationType,
          
          // Информация о местных сопротивлениях
          localResistanceCoefficients,
          totalLocalResistance,
          includeLocalResistance,
          
          // Информация о перепаде высот
          elevationChange,
          includeElevationChange,
          
          // Рекомендуемый стандартный диаметр (только для расчета диаметра)
          standardRecommendedDiameter
        };
        
        setResults(finalResults);
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
        setResults(null);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, setErrors, setResults, setIsCalculating, pipeTypes, standardDiameters]);

  // Сброс формы
  const resetForm = () => {
    setFormData({
      pipeType: 'steel',
      pipeInnerDiameter: '40',
      pipeLength: '100',
      pipeRoughness: '0.15',
      systemPressure: '4',
      waterTemperature: '15',
      calculationType: 'flowRate',
      pressureDrop: '0.5',
      flowRate: '2.5',
      requiredFlowRate: '3.0',
      maxPressureDrop: '0.8',
      includeLocalResistance: false,
      bendCount: '4',
      valveCount: '2',
      teeCount: '1',
      entranceType: 'standard',
      exitType: 'standard',
      includeElevationChange: false,
      elevationChange: '2',
    });
    setResults(null);
    setErrors({});
    setSaveCalcName('');
    setShowSaveDialog(false);
  };

  // Экспорт результатов в CSV
  const exportToCSV = () => {
    if (!results) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    const headers = ['Параметр', 'Значение', 'Единица измерения'];
    
    // Подготовка данных для экспорта в зависимости от типа расчета
    const rows = [];
    
    // Добавление основных входных параметров
    rows.push(['Тип трубы', pipeTypes.find(t => t.id === results.pipeType)?.name || results.pipeType, '']);
    rows.push(['Внутренний диаметр', results.pipeInnerDiameter.toFixed(2), 'мм']);
    rows.push(['Длина трубы', results.pipeLength.toFixed(2), 'м']);
    rows.push(['Шероховатость', results.pipeRoughness.toFixed(4), 'мм']);
    rows.push(['Давление в системе', results.systemPressure.toFixed(2), 'бар']);
    rows.push(['Температура воды', results.waterTemperature.toFixed(1), '°C']);
    
    // Добавление результатов расчета
    rows.push(['', '', '']);
    rows.push(['РЕЗУЛЬТАТЫ РАСЧЕТА', '', '']);
    
    if (results.calculationType === 'flowRate') {
      rows.push(['Перепад давления (вход)', parseFloatOrDefault(formData.pressureDrop).toFixed(3), 'бар']);
      rows.push(['Расход воды (расчет)', results.flowRate.toFixed(3), 'м³/ч']);
      rows.push(['Скорость потока', results.velocity.toFixed(3), 'м/с']);
    } 
    else if (results.calculationType === 'pressure') {
      rows.push(['Расход воды (вход)', parseFloatOrDefault(formData.flowRate).toFixed(3), 'м³/ч']);
      rows.push(['Перепад давления (расчет)', results.pressureDrop.toFixed(3), 'бар']);
      rows.push(['Скорость потока', results.velocity.toFixed(3), 'м/с']);
    } 
    else if (results.calculationType === 'diameter') {
      rows.push(['Требуемый расход (вход)', parseFloatOrDefault(formData.requiredFlowRate).toFixed(3), 'м³/ч']);
      rows.push(['Максимальный перепад (вход)', parseFloatOrDefault(formData.maxPressureDrop).toFixed(3), 'бар']);
      rows.push(['Расчетный диаметр', results.diameter ? (results.diameter * 1000).toFixed(2) : 'N/A', 'мм']);
      rows.push(['Рекомендуемый стандартный диаметр', results.standardRecommendedDiameter ? results.standardRecommendedDiameter.toFixed(1) : 'N/A', 'мм']);
      rows.push(['Фактический перепад давления', results.actualPressureDrop ? results.actualPressureDrop.toFixed(3) : 'N/A', 'бар']);
      rows.push(['Скорость потока', results.velocity ? results.velocity.toFixed(3) : 'N/A', 'м/с']);
    }
    
    // Добавление информации о гидравлическом режиме
    rows.push(['Число Рейнольдса', results.reynoldsNumber.toFixed(0), '']);
    rows.push(['Режим течения', results.flowType, '']);
    
    // Добавление информации о местных сопротивлениях
    if (results.includeLocalResistance) {
      rows.push(['', '', '']);
      rows.push(['МЕСТНЫЕ СОПРОТИВЛЕНИЯ', '', '']);
      rows.push(['Повороты 90°', formData.bendCount, 'шт.']);
      rows.push(['Вентили', formData.valveCount, 'шт.']);
      rows.push(['Тройники', formData.teeCount, 'шт.']);
      rows.push(['Коэффициент на входе', results.localResistanceCoefficients.entranceResistance.toFixed(2), '']);
      rows.push(['Коэффициент на выходе', results.localResistanceCoefficients.exitResistance.toFixed(2), '']);
      rows.push(['Суммарное сопротивление', results.totalLocalResistance.toFixed(2), '']);
    }
    
    // Добавление информации о перепаде высот
    if (results.includeElevationChange) {
      rows.push(['', '', '']);
      rows.push(['ПЕРЕПАД ВЫСОТ', '', '']);
      rows.push(['Изменение высоты', results.elevationChange.toFixed(2), 'м']);
      rows.push(['Гидростатический напор', (results.elevationChange * 0.0981).toFixed(3), 'бар']);
    }
    
    // Добавляем мета-информацию
    const infoRows = [
      ['Калькулятор расхода воды в трубах', '', ''],
      ['Дата расчета:', new Date().toLocaleDateString('ru-RU'), ''],
      ['', '', ''],
    ];

    // UTF-8 BOM для правильной кодировки в Excel
    const csvContent = '\uFEFF' + 
      [...infoRows, headers, ...rows]
        .map(row => row.join(';'))
        .join('\n');
        
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'расчет_труб.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Обработчик сохранения расчета
  const handleSaveCalculation = () => {
    if (showSaveDialog) {
      const savedResult = saveCalculation(saveCalcName || null);
      if (savedResult) {
        alert(`Расчет "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveCalcName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculateResults();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <Droplet className="w-6 h-6 text-blue-500" />
          Параметры расчета
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Выбор типа расчета */}
        <CalculationTypeSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors}
        />

        {/* Основные параметры трубы */}
        <PipeParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          pipeTypes={pipeTypes}
        />

        {/* Дополнительные параметры */}
        <AdditionalParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          calculationType={formData.calculationType}
        />

        {/* Местные сопротивления */}
        <LocalResistanceSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          connectionTypes={connectionTypes}
        />

        {/* Перепад высот */}
        <ElevationSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Диалог сохранения расчета */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveCalcName}
                onChange={(e) => setSaveCalcName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveCalculation}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<Calculator className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Рассчитываем...' : 'Рассчитать'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {results && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Скачать результаты (CSV)
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveCalculation}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить расчет
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;